// /app/frontend/src/components/admin/PhotoComparisonSlider.jsx
// Side-by-side before/after photo comparison with draggable slider

import React, { useState, useRef, useCallback, useEffect } from 'react';
import { X, ZoomIn, ZoomOut, ChevronLeft, ChevronRight, Move } from 'lucide-react';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';

const API = process.env.REACT_APP_BACKEND_URL;

export default function PhotoComparisonSlider({ 
  isOpen, 
  onClose, 
  beforePhotos = [], 
  afterPhotos = [],
  eventTitle = 'Job'
}) {
  const [sliderPosition, setSliderPosition] = useState(50);
  const [isDragging, setIsDragging] = useState(false);
  const [beforeIndex, setBeforeIndex] = useState(0);
  const [afterIndex, setAfterIndex] = useState(0);
  const [zoom, setZoom] = useState(1);
  const containerRef = useRef(null);

  const beforePhoto = beforePhotos[beforeIndex];
  const afterPhoto = afterPhotos[afterIndex];

  const handleMouseDown = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleMouseMove = useCallback((e) => {
    if (!isDragging || !containerRef.current) return;
    
    const rect = containerRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percentage = Math.max(0, Math.min(100, (x / rect.width) * 100));
    setSliderPosition(percentage);
  }, [isDragging]);

  const handleTouchMove = useCallback((e) => {
    if (!containerRef.current) return;
    
    const touch = e.touches[0];
    const rect = containerRef.current.getBoundingClientRect();
    const x = touch.clientX - rect.left;
    const percentage = Math.max(0, Math.min(100, (x / rect.width) * 100));
    setSliderPosition(percentage);
  }, []);

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mouseup', handleMouseUp);
      document.addEventListener('mousemove', handleMouseMove);
      return () => {
        document.removeEventListener('mouseup', handleMouseUp);
        document.removeEventListener('mousemove', handleMouseMove);
      };
    }
  }, [isDragging, handleMouseUp, handleMouseMove]);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setSliderPosition(50);
      setBeforeIndex(0);
      setAfterIndex(0);
      setZoom(1);
    }
  }, [isOpen]);

  const getPhotoUrl = (photo) => {
    if (photo?.storage_url) return photo.storage_url;
    if (photo?.id) return `${API}/api/schedule/photos/${photo.id}`;
    return null;
  };

  if (!beforePhoto && !afterPhoto) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Photo Comparison</DialogTitle>
          </DialogHeader>
          <div className="text-center py-8 text-gray-500">
            <Move className="h-12 w-12 mx-auto mb-3 text-gray-300" />
            <p>Need both before and after photos to compare.</p>
            <p className="text-sm mt-2">
              Before photos: {beforePhotos.length} | After photos: {afterPhotos.length}
            </p>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl w-[95vw] p-0 overflow-hidden">
        <DialogHeader className="p-4 pb-2 border-b">
          <div className="flex items-center justify-between">
            <DialogTitle className="flex items-center gap-2">
              <Move className="h-5 w-5" />
              Before & After Comparison - {eventTitle}
            </DialogTitle>
            <div className="flex items-center gap-2">
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setZoom(z => Math.max(0.5, z - 0.25))}
                data-testid="comparison-zoom-out"
              >
                <ZoomOut className="h-4 w-4" />
              </Button>
              <span className="text-sm text-gray-500 w-12 text-center">{Math.round(zoom * 100)}%</span>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setZoom(z => Math.min(3, z + 0.25))}
                data-testid="comparison-zoom-in"
              >
                <ZoomIn className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </DialogHeader>

        {/* Comparison Container */}
        <div 
          ref={containerRef}
          className="relative w-full h-[60vh] bg-gray-900 overflow-hidden cursor-ew-resize select-none"
          onMouseMove={handleMouseMove}
          onTouchMove={handleTouchMove}
          data-testid="comparison-container"
        >
          {/* After Photo (Full width, behind) */}
          {afterPhoto && (
            <div className="absolute inset-0 flex items-center justify-center">
              <img
                src={getPhotoUrl(afterPhoto)}
                alt="After"
                className="max-w-full max-h-full object-contain"
                style={{ transform: `scale(${zoom})` }}
                draggable={false}
              />
              <div className="absolute top-4 right-4 bg-green-600 text-white px-3 py-1 rounded-full text-sm font-medium">
                AFTER
              </div>
            </div>
          )}

          {/* Before Photo (Clipped) */}
          {beforePhoto && (
            <div 
              className="absolute inset-0 flex items-center justify-center overflow-hidden"
              style={{ width: `${sliderPosition}%` }}
            >
              <div className="relative w-full h-full flex items-center justify-center" style={{ width: `${100 / (sliderPosition / 100)}%` }}>
                <img
                  src={getPhotoUrl(beforePhoto)}
                  alt="Before"
                  className="max-w-full max-h-full object-contain"
                  style={{ transform: `scale(${zoom})` }}
                  draggable={false}
                />
              </div>
              <div className="absolute top-4 left-4 bg-blue-600 text-white px-3 py-1 rounded-full text-sm font-medium">
                BEFORE
              </div>
            </div>
          )}

          {/* Slider Handle */}
          <div 
            className="absolute top-0 bottom-0 w-1 bg-white cursor-ew-resize z-20"
            style={{ left: `${sliderPosition}%`, transform: 'translateX(-50%)' }}
            onMouseDown={handleMouseDown}
            onTouchStart={() => setIsDragging(true)}
            onTouchEnd={() => setIsDragging(false)}
          >
            {/* Handle Circle */}
            <div 
              className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-10 h-10 bg-white rounded-full shadow-lg flex items-center justify-center cursor-grab active:cursor-grabbing"
              onMouseDown={handleMouseDown}
            >
              <div className="flex items-center gap-0.5">
                <ChevronLeft className="h-4 w-4 text-gray-600" />
                <ChevronRight className="h-4 w-4 text-gray-600" />
              </div>
            </div>
          </div>

          {/* No before photo placeholder */}
          {!beforePhoto && (
            <div 
              className="absolute inset-0 flex items-center justify-center bg-gray-800"
              style={{ width: `${sliderPosition}%` }}
            >
              <div className="text-center text-gray-400">
                <p>No before photo</p>
              </div>
            </div>
          )}

          {/* No after photo placeholder */}
          {!afterPhoto && (
            <div className="absolute inset-0 flex items-center justify-center bg-gray-800">
              <div className="text-center text-gray-400">
                <p>No after photo</p>
              </div>
            </div>
          )}
        </div>

        {/* Photo Navigation */}
        <div className="p-4 border-t bg-gray-50 flex items-center justify-between">
          {/* Before photo selector */}
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-blue-600">Before:</span>
            <div className="flex gap-1">
              {beforePhotos.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => setBeforeIndex(idx)}
                  className={`w-8 h-8 rounded border-2 transition-colors ${
                    beforeIndex === idx 
                      ? 'border-blue-500 bg-blue-100' 
                      : 'border-gray-200 hover:border-blue-300'
                  }`}
                >
                  {idx + 1}
                </button>
              ))}
              {beforePhotos.length === 0 && (
                <span className="text-sm text-gray-400">None</span>
              )}
            </div>
          </div>

          {/* Slider position indicator */}
          <div className="text-sm text-gray-500">
            Drag slider to compare
          </div>

          {/* After photo selector */}
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-green-600">After:</span>
            <div className="flex gap-1">
              {afterPhotos.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => setAfterIndex(idx)}
                  className={`w-8 h-8 rounded border-2 transition-colors ${
                    afterIndex === idx 
                      ? 'border-green-500 bg-green-100' 
                      : 'border-gray-200 hover:border-green-300'
                  }`}
                >
                  {idx + 1}
                </button>
              ))}
              {afterPhotos.length === 0 && (
                <span className="text-sm text-gray-400">None</span>
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
